import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import WorkModalShell from '@/components/common/WorkModalShell';
import { Input, Select, Textarea } from '@/components/ui/FormControls';
import {
  deliveryLabel,
  deliveryRpc,
  deliverySnapshot,
  nextDeliveryStages,
  type DeliveryRow,
  type DeliveryOutcome,
} from '@/lib/deliveries';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import { cn } from '@/lib/utils';

export const deliveryButton =
  'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-xl border border-border bg-background px-3 text-sm font-medium transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-not-allowed disabled:opacity-50';
export default function DeliveryUpdateDialog({
  rows,
  onClose,
  onComplete,
}: {
  rows: DeliveryRow[];
  onClose: () => void;
  onComplete: (results: DeliveryOutcome[]) => void;
}) {
  const { role, can } = useAuth();
  const client = useQueryClient();
  const [action, setAction] = useState('status_change');
  const stages = useMemo(
    () =>
      rows.length
        ? nextDeliveryStages(rows[0]!, action === 'correct_status').filter((s) =>
            rows.every((row) => nextDeliveryStages(row, action === 'correct_status').includes(s)),
          )
        : [],
    [rows, action],
  );
  const [target, setTarget] = useState('');
  const targetStage = stages.includes(target as (typeof stages)[number]) ? target : stages[0] || '';
  const [carrier, setCarrier] = useState(rows.length === 1 ? rows[0]!.courier_name || '' : '');
  const [website, setWebsite] = useState(rows.length === 1 ? rows[0]!.website_url || '' : '');
  const [details, setDetails] = useState<Record<string, { tracking: string; url: string }>>(() =>
    Object.fromEntries(
      rows.map((r) => [
        r.sale_id,
        { tracking: r.tracking_number || '', url: r.tracking_url || '' },
      ]),
    ),
  );
  const [address, setAddress] = useState('');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [issue, setIssue] = useState('delay');
  const [occurred, setOccurred] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<DeliveryOutcome[] | null>(null);
  const request = useRef({ signature: '', key: '' });
  const inFlight = useRef(false);
  const operation = useRef<AbortController | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      operation.current?.abort();
    };
  }, []);
  const dirty = !!(
    reason ||
    note ||
    occurred ||
    address ||
    target ||
    action !== 'status_change' ||
    carrier !== (rows.length === 1 ? rows[0]!.courier_name || '' : '') ||
    website !== (rows.length === 1 ? rows[0]!.website_url || '' : '') ||
    rows.some(
      (row) =>
        details[row.sale_id]!.tracking !== (row.tracking_number || '') ||
        details[row.sale_id]!.url !== (row.tracking_url || ''),
    )
  );
  const cancel = () => {
    if (!dirty || window.confirm('Discard your unsaved delivery changes?')) onClose();
  };
  const courierFields =
    (action === 'status_change' && targetStage === 'dispatched') || action === 'courier_update';
  const addressNeeded =
    (action === 'status_change' && ['packed', 'dispatched', 'delivered'].includes(targetStage)) ||
    action === 'reverify_packing';
  const needsReason =
    ['correct_status', 'set_issue'].includes(action) ||
    (action === 'status_change' && targetStage === 'cancelled');
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (inFlight.current || !can('sales.status')) return;
    setError('');
    inFlight.current = true;
    setBusy(true);
    const controller = new AbortController();
    operation.current = controller;
    try {
      const payload = {
        action,
        target_stage:
          action === 'status_change' || action === 'correct_status' ? targetStage : undefined,
        reason: needsReason ? reason.trim() : undefined,
        internal_note: note || undefined,
        issue: action === 'set_issue' ? issue : undefined,
        occurred_at: occurred ? new Date(occurred).toISOString() : undefined,
        items: rows.map((row) => ({
          ...deliverySnapshot(row),
          address_confirmation: address || undefined,
          courier: courierFields
            ? {
                name: carrier || row.courier_name,
                website_url: rows.length === 1 ? website : website || row.website_url,
                tracking_number: details[row.sale_id]!.tracking,
                tracking_url: details[row.sale_id]!.url,
              }
            : undefined,
        })),
      };
      const signature = JSON.stringify(payload);
      if (request.current.signature !== signature)
        request.current = { signature, key: crypto.randomUUID() };
      const response = await deliveryRpc<{ results: DeliveryOutcome[] }>(
        'bulk_update_sale_deliveries',
        { p_data: { ...payload, request_key: request.current.key } },
        controller.signal,
      );
      if (!alive.current || controller.signal.aborted) return;
      if (
        response.results.length !== rows.length ||
        response.results.some((r) => !rows.some((row) => row.sale_id === r.sale_id))
      )
        throw new Error(
          'Incomplete update results. Refresh and check each delivery before retrying.',
        );
      const needsCourierPatch = (result: DeliveryOutcome) =>
        action === 'courier_update' &&
        result.outcome === 'failed' &&
        result.message ===
          'An Owner or Admin and reason are required to correct dispatched tracking';
      const courierPatchMessage =
        'The courier-update database patch is required. Ask an administrator to apply it before editing dispatched courier details.';
      if (response.results.every(needsCourierPatch)) {
        setError(`${courierPatchMessage} Your entries are kept.`);
        return;
      }
      setResults(
        response.results.map((result) =>
          needsCourierPatch(result)
            ? {
                ...result,
                message: courierPatchMessage,
              }
            : result,
        ),
      );
      onComplete(response.results);
      await refreshSaleConsumers(client);
    } catch (e) {
      if (!alive.current) return;
      setError(e instanceof Error ? e.message : 'Delivery update failed. Your entries are kept.');
    } finally {
      inFlight.current = false;
      operation.current = null;
      if (alive.current) setBusy(false);
    }
  }
  return (
    <WorkModalShell
      open
      onClose={onClose}
      title="Update delivery"
      busy={busy}
      dirty={!results && dirty}
      wide
    >
      <header className="flex shrink-0 items-start justify-between gap-3 border-b border-border py-3">
        <div className="min-w-0">
          <h2 className="break-words text-lg font-semibold">
            Update {rows.length === 1 ? rows[0]!.sale_number : `${rows.length} deliveries`}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Review the selected shipments. Each courier parcel keeps its own tracking number.
          </p>
        </div>
        <button
          type="button"
          aria-label="Close delivery update"
          disabled={busy}
          onClick={cancel}
          className={cn(deliveryButton, 'h-11 w-11 shrink-0 p-0')}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </header>
      {results ? (
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-4">
          <p role="status" className="font-semibold">
            {results.filter((r) => ['updated', 'unchanged'].includes(r.outcome)).length} of{' '}
            {results.length} completed
          </p>
          <ul className="space-y-2">
            {results.map((r) => (
              <li key={r.sale_id} className="rounded-xl border border-border p-3 text-sm">
                <strong>
                  {rows.find((row) => row.sale_id === r.sale_id)?.sale_number}:{' '}
                  {deliveryLabel(r.outcome)}
                </strong>
                {r.message && <p className="mt-1 text-destructive">{r.message}</p>}
              </li>
            ))}
          </ul>
          <p className="text-sm text-muted-foreground">
            Unsuccessful rows stay selected. Close, refresh and review their current details before
            trying again.
          </p>
          <button className={deliveryButton} onClick={onClose}>
            Done
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-4 [scroll-padding-block:1rem]">
            <fieldset disabled={busy} className="min-w-0 space-y-4">
              <label className="block space-y-1 text-sm">
                <span>Action</span>
                <Select
                  className="w-full"
                  value={action}
                  onChange={(e) => {
                    setAction(e.target.value);
                    setTarget('');
                  }}
                >
                  <option value="status_change">Change status</option>
                  <option value="courier_update">Update courier details</option>
                  <option value="set_issue">Flag a delivery issue</option>
                  <option value="resolve_issue">Resolve delivery issue</option>
                  <option value="reverify_packing">Recheck packed items and address</option>
                  {['owner', 'admin'].includes(role || '') && (
                    <option value="correct_status">Correct delivery stage</option>
                  )}
                </Select>
              </label>
              {['status_change', 'correct_status'].includes(action) && (
                <label className="block space-y-1 text-sm">
                  <span>New status</span>
                  <Select
                    className="w-full"
                    required
                    value={targetStage}
                    onChange={(e) => setTarget(e.target.value)}
                  >
                    <option value="" disabled>
                      Choose a status
                    </option>
                    {stages.map((s) => (
                      <option key={s} value={s}>
                        {deliveryLabel(s)}
                      </option>
                    ))}
                  </Select>
                  {!stages.length && (
                    <p className="text-amber-600">
                      These shipments have no common next status. Select shipments at the same
                      stage.
                    </p>
                  )}
                </label>
              )}
              {action === 'correct_status' && (
                <p className="rounded-xl bg-amber-500/10 p-3 text-sm">
                  A delivered reversal restores and reserves stock through the existing Sale
                  workflow. For customer returns, use Sales → Return instead.
                </p>
              )}
              {addressNeeded && (
                <label className="block space-y-1 text-sm">
                  <span>Address check for these parcels</span>
                  <Select
                    className="w-full"
                    required
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                  >
                    <option value="" disabled>
                      Choose after checking each parcel
                    </option>
                    <option value="saved_address">
                      Saved recipient address is complete and checked
                    </option>
                    <option value="written_on_parcel">
                      Full recipient address is written on every parcel
                    </option>
                  </Select>
                </label>
              )}
              {courierFields && (
                <section className="space-y-3 rounded-xl border border-border p-3">
                  <h3 className="font-semibold">Courier details</h3>
                  <label className="block text-sm">
                    Courier company {rows.length > 1 && '(shared; blank keeps each saved company)'}
                    <Input
                      value={carrier}
                      maxLength={120}
                      required={rows.length === 1 && rows[0]!.fulfillment_method === 'courier'}
                      onChange={(e) => setCarrier(e.target.value)}
                    />
                  </label>
                  <label className="block text-sm">
                    Courier website (optional HTTPS URL)
                    <Input
                      type="url"
                      value={website}
                      maxLength={1000}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </label>
                  {rows.map((row) => (
                    <div key={row.sale_id} className="space-y-2 rounded-lg bg-muted/40 p-3">
                      <p className="text-sm font-semibold">
                        {row.sale_number} · {row.customer_name || 'Customer'} ·{' '}
                        {deliveryLabel(row.fulfillment_method)}
                      </p>
                      <label className="block text-sm">
                        Tracking number {row.fulfillment_method === 'courier' ? '*' : '(optional)'}
                        <Input
                          value={details[row.sale_id]!.tracking}
                          required={row.fulfillment_method === 'courier'}
                          maxLength={160}
                          onChange={(e) =>
                            setDetails({
                              ...details,
                              [row.sale_id]: { ...details[row.sale_id]!, tracking: e.target.value },
                            })
                          }
                        />
                      </label>
                      <label className="block text-sm">
                        Exact tracking link (optional HTTPS URL)
                        <Input
                          type="url"
                          value={details[row.sale_id]!.url}
                          maxLength={1000}
                          onChange={(e) =>
                            setDetails({
                              ...details,
                              [row.sale_id]: { ...details[row.sale_id]!, url: e.target.value },
                            })
                          }
                        />
                      </label>
                    </div>
                  ))}
                </section>
              )}
              {action === 'set_issue' && (
                <label className="block text-sm">
                  Issue
                  <Select
                    className="w-full"
                    value={issue}
                    onChange={(e) => setIssue(e.target.value)}
                  >
                    <option value="delay">Delay</option>
                    <option value="failed_attempt">Failed delivery attempt</option>
                    <option value="return_to_origin">
                      Return to origin (no stock/refund action)
                    </option>
                    <option value="other">Other</option>
                  </Select>
                </label>
              )}
              {needsReason && (
                <label className="block text-sm">
                  Reason *
                  <Textarea
                    value={reason}
                    required={needsReason}
                    maxLength={1000}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <span className="mt-1 block text-xs text-muted-foreground">
                    Required for cancellations, delivery issues and stage corrections.
                  </span>
                </label>
              )}
              <label className="block text-sm">
                Internal note (not shared with customer)
                <Textarea value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
              </label>
              <label className="block text-sm">
                Actual event time (optional; local time)
                <Input
                  type="datetime-local"
                  value={occurred}
                  onChange={(e) => setOccurred(e.target.value)}
                />
              </label>
              <p className="text-xs text-muted-foreground">
                Blank time records now. This action records a timeline event; WhatsApp is opened
                separately for your review.
              </p>
            </fieldset>
          </div>
          {error && (
            <p
              role="alert"
              className="max-h-24 shrink-0 overflow-y-auto py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}
          <footer className="flex shrink-0 justify-end gap-2 border-t border-border bg-card pt-3">
            <button type="button" disabled={busy} className={deliveryButton} onClick={cancel}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                busy ||
                !can('sales.status') ||
                (['status_change', 'correct_status'].includes(action) && !targetStage)
              }
              className={cn(
                deliveryButton,
                'bg-primary text-primary-foreground hover:bg-primary/90',
              )}
            >
              {busy ? 'Updating…' : 'Confirm update'}
            </button>
          </footer>
        </form>
      )}
    </WorkModalShell>
  );
}
